import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { TrendingUp, Mail, Lock, Eye, EyeOff } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  const from = location.state?.from?.pathname || '/dashboard';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    
    const result = await login(email, password);
    
    if (result.success) {
      navigate(from, { replace: true });
    } else {
      setError(result.error);
    }
    
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-[#09090B] flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        {/* Logo */}
        <Link to="/" className="flex items-center justify-center gap-2 mb-8" data-testid="login-logo-link">
          <div className="w-10 h-10 bg-[#007AFF] rounded-sm flex items-center justify-center">
            <TrendingUp className="w-6 h-6 text-white" />
          </div>
          <span className="font-bold text-xl tracking-tight text-white" style={{ fontFamily: 'Chivo, sans-serif' }}>
            COSSY NETWORK
          </span>
        </Link>
        
        {/* Login Card */}
        <div className="bg-[#141417] border border-[#27272A] rounded-md p-8">
          <div className="text-center mb-8">
            <h1 className="text-2xl font-bold text-white mb-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
              Welcome Back
            </h1>
            <p className="text-[#71717A]">Sign in to access your dashboard</p>
          </div>
          
          {error && (
            <div className="bg-[#FF3333]/10 border border-[#FF3333]/30 text-[#FF3333] px-4 py-3 rounded-sm mb-6 text-sm" data-testid="login-error">
              {error}
            </div>
          )}
          
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <Label htmlFor="email" className="text-[#A1A1AA]">Email</Label>
              <div className="relative mt-2">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#71717A]" />
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="input-dark pl-10"
                  required
                  data-testid="login-email-input"
                />
              </div>
            </div>
            
            <div>
              <Label htmlFor="password" className="text-[#A1A1AA]">Password</Label>
              <div className="relative mt-2">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#71717A]" />
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="input-dark pl-10 pr-10"
                  required
                  data-testid="login-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#71717A] hover:text-white"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>
            
            <div className="flex items-center justify-between">
              <Link to="/forgot-password" className="text-sm text-[#007AFF] hover:underline" data-testid="forgot-password-link">
                Forgot password?
              </Link>
            </div>
            
            <Button
              type="submit"
              className="btn-primary w-full"
              disabled={loading}
              data-testid="login-submit-btn"
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </Button>
          </form>
          
          <p className="text-center mt-6 text-[#71717A]">
            Don't have an account?{' '}
            <Link to="/register" className="text-[#007AFF] hover:underline" data-testid="register-link">
              Sign up
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
